import React, { useState, useEffect, useCallback } from 'react';
import SALayout from './SALayout';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Loader2, ToggleLeft, Building2, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SAFeatureFlags() {
  const { token } = useSuperAdmin();
  const [flags, setFlags] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFlag, setExpandedFlag] = useState(null);

  const apiFetch = useCallback((path, opts = {}) =>
    fetch(`${API_URL}/api/superadmin${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...opts.headers }
    }), [token]);

  const loadData = useCallback(async () => {
    try {
      const [flagsRes, orgsRes] = await Promise.all([
        apiFetch('/feature-flags'),
        apiFetch('/organizations')
      ]);
      setFlags(await flagsRes.json());
      setOrgs(await orgsRes.json());
    } catch { toast.error('Failed to load data'); } finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleGlobal = async (key, currentVal) => {
    const res = await apiFetch(`/feature-flags/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ global_default: !currentVal })
    });
    if (res.ok) {
      setFlags(prev => prev.map(f => f.key === key ? { ...f, global_default: !currentVal } : f));
      toast.success(`${key} is now ${!currentVal ? 'ON' : 'OFF'} globally`);
    }
  };

  const setOrgOverride = async (key, orgId, enabled) => {
    const res = await apiFetch(`/feature-flags/${key}/orgs/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled })
    });
    if (res.ok) {
      setFlags(prev => prev.map(f => {
        if (f.key !== key) return f;
        const newOverrides = { ...f.org_overrides, [orgId]: enabled };
        return { ...f, org_overrides: newOverrides, override_count: Object.keys(newOverrides).length };
      }));
      toast.success('Override set');
    }
  };

  const removeOverride = async (key, orgId) => {
    const res = await apiFetch(`/feature-flags/${key}/orgs/${orgId}`, { method: 'DELETE' });
    if (res.ok) {
      setFlags(prev => prev.map(f => {
        if (f.key !== key) return f;
        const newOverrides = { ...f.org_overrides };
        delete newOverrides[orgId];
        return { ...f, org_overrides: newOverrides, override_count: Object.keys(newOverrides).length };
      }));
      toast.success('Override removed');
    }
  };

  if (loading) {
    return (
      <SALayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </SALayout>
    );
  }

  return (
    <SALayout>
      <div className="space-y-6" data-testid="sa-feature-flags">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Feature Flags</h1>
          <p className="text-sm text-slate-500 mt-1">Control which features are available to organizations. Toggle globally or set per-org overrides.</p>
        </div>

        <div className="space-y-3">
          {flags.map(flag => {
            const isExpanded = expandedFlag === flag.key;
            const overrideEntries = Object.entries(flag.org_overrides || {});

            return (
              <Card key={flag.key} className="overflow-hidden" data-testid={`ff-card-${flag.key}`}>
                <CardContent className="p-0">
                  {/* Flag Header */}
                  <div className="flex items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900" data-testid={`ff-label-${flag.key}`}>{flag.label}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${flag.global_default ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {flag.global_default ? 'ON' : 'OFF'}
                        </span>
                        {flag.override_count > 0 && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                            {flag.override_count} override{flag.override_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{flag.description}</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        checked={flag.global_default}
                        onCheckedChange={() => toggleGlobal(flag.key, flag.global_default)}
                        data-testid={`ff-toggle-${flag.key}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-slate-400 hover:text-slate-700"
                        onClick={() => setExpandedFlag(isExpanded ? null : flag.key)}
                        data-testid={`ff-expand-${flag.key}`}
                      >
                        <Building2 className="w-3.5 h-3.5 mr-1" />
                        <span className="text-xs">Orgs</span>
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 ml-1" /> : <ChevronRight className="w-3.5 h-3.5 ml-1" />}
                      </Button>
                    </div>
                  </div>

                  {/* Per-Org Overrides */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50 px-5 py-3">
                      <p className="text-xs font-medium text-slate-500 mb-3">Per-Organization Overrides</p>

                      {/* Existing overrides */}
                      {overrideEntries.length > 0 && (
                        <div className="space-y-1.5 mb-3">
                          {overrideEntries.map(([orgId, enabled]) => {
                            const org = orgs.find(o => o.id === orgId);
                            return (
                              <div key={orgId} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100">
                                <span className="text-xs font-medium text-slate-700">{org?.name || orgId}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-bold uppercase ${enabled ? 'text-green-600' : 'text-red-500'}`}>
                                    {enabled ? 'ON' : 'OFF'}
                                  </span>
                                  <Switch
                                    checked={enabled}
                                    onCheckedChange={(val) => setOrgOverride(flag.key, orgId, val)}
                                    className="scale-75"
                                  />
                                  <button
                                    onClick={() => removeOverride(flag.key, orgId)}
                                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors"
                                    title="Remove override (use global default)"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add override for an org */}
                      <OrgOverrideAdder
                        orgs={orgs}
                        existingOverrides={flag.org_overrides || {}}
                        onAdd={(orgId, enabled) => setOrgOverride(flag.key, orgId, enabled)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </SALayout>
  );
}

// Small helper component for adding a new org override
function OrgOverrideAdder({ orgs, existingOverrides, onAdd }) {
  const [selectedOrg, setSelectedOrg] = useState('');
  const availableOrgs = orgs.filter(o => !(o.id in existingOverrides));

  if (availableOrgs.length === 0) {
    return <p className="text-[11px] text-slate-400">All organizations have overrides set.</p>;
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={selectedOrg}
        onChange={e => setSelectedOrg(e.target.value)}
        className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-300"
        data-testid="ff-org-select"
      >
        <option value="">Select organization...</option>
        {availableOrgs.map(o => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs text-green-600 border-green-200 hover:bg-green-50"
        disabled={!selectedOrg}
        onClick={() => { onAdd(selectedOrg, true); setSelectedOrg(''); }}
        data-testid="ff-add-override-on"
      >
        Enable
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
        disabled={!selectedOrg}
        onClick={() => { onAdd(selectedOrg, false); setSelectedOrg(''); }}
        data-testid="ff-add-override-off"
      >
        Disable
      </Button>
    </div>
  );
}
